import { Injectable } from '@angular/core';
import * as DOMPurify from 'dompurify';

@Injectable({
  providedIn: 'root',
})
export class XssSanitizerService {
  constructor() {}

  /** IMAGE INPUT SANITIZER **/
  sanitizeImageInput(image: any): any {
    const allowedMimeTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

    // Base64 image string
    if (typeof image === 'string' && image.startsWith('data:image/')) {
      const mimeMatch = image.match(/^data:(image\/[a-zA-Z+]+);base64,/);
      const mimeType = mimeMatch ? mimeMatch[1].toLowerCase() : '';

      const base64Data = image.split(',')[1] || '';
      const isValidBase64 = /^[A-Za-z0-9+/]+={0,2}$/.test(base64Data);
      const isRealImage = base64Data.length > 100;

      if (!allowedMimeTypes.includes(mimeType) || !isValidBase64 || !isRealImage) {
        return '';
      }
      return image;
    }

    // URL string
    if (typeof image === 'string') {
      const imageUrlPattern = /^(https?:\/\/.*\.(?:png|jpe?g|gif|webp))$/i;
      return imageUrlPattern.test(image) ? image : '';
    }

    // File object
    if (image instanceof File) {
      return image;
    }

    // Unknown type
    return '';
  }

  /** GENERIC STRING SANITIZER **/
  sanitizeInput(value: string): string {
    if (!value || typeof value !== 'string') return '';

    // Remove encoded and real <script> tags
    value = value.replace(/<script.*?>[\s\S]*?<\/script>/gi, '');
    value = value.replace(/&lt;script.*?&gt;[\s\S]*?&lt;\/script&gt;/gi, '');

    return DOMPurify.sanitize(value, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: [],
      FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'link', 'style'],
      FORBID_ATTR: ['onerror', 'onclick', 'onload', 'onmouseover'],
    }).trim();
  }

  /** USED FOR REAL EDITOR (STRICT SANITIZER ON SAVE) **/
  sanitizeEditorInput(value: string): string {
    if (!value || typeof value !== 'string') return '';

    // Decode encoded HTML entities
    const decodeHtmlEntities = (input: string): string => {
      const txt = document.createElement('textarea');
      txt.innerHTML = input;
      return txt.value;
    };
    value = decodeHtmlEntities(value);

    // Remove comments (prevents <!-- --!> tricks)
    value = value.replace(/<!--[\s\S]*?-->/g, '');

    // Remove script tags
    value = value.replace(/<script.*?>[\s\S]*?<\/script>/gi, '');

    // Sanitize safely
    const cleaned = DOMPurify.sanitize(value, {
      ALLOWED_TAGS: ['p', 'b', 'i', 'em', 'strong', 'a', 'ul', 'ol', 'li', 'br', 'span', 'div'],
      ALLOWED_ATTR: ['href', 'title', 'class'],
      FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'style', 'link'],
      FORBID_ATTR: ['onerror', 'onclick', 'onload', 'onmouseover', 'style'],
    }).trim();

    return cleaned || '';
  }

  /** LIGHT SANITIZER (FOR LIVE TYPING, DOESN’T BLOCK INPUT) **/
  sanitizeEditorWhileTyping(value: string): string {
    if (!value || typeof value !== 'string') return value;

    // Light sanitization — no comment stripping so typing feels smooth
    return DOMPurify.sanitize(value, {
      ALLOWED_TAGS: ['p', 'b', 'i', 'em', 'strong', 'a', 'ul', 'ol', 'li', 'br', 'span', 'div', 'img'],
      ALLOWED_ATTR: ['href', 'title', 'class', 'src', 'alt'],
      FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'style', 'link'],
      FORBID_ATTR: ['onerror', 'onclick', 'onload', 'onmouseover', 'style'],
    });
  }

  /** RECURSIVE PAYLOAD SANITIZER (OBJECTS, ARRAYS, STRINGS) **/
  sanitizePayload(obj: any): any {
    if (!obj || typeof obj !== 'object') return obj;

    const sanitized: any = Array.isArray(obj) ? [] : {};

    for (const key of Object.keys(obj)) {
      const value = obj[key];

      if (key.toLowerCase().includes('image')) {
        sanitized[key] = this.sanitizeImageInput(value);
      } else if (key.toLowerCase().includes('editor') || key.toLowerCase().includes('html')) {
        sanitized[key] = this.sanitizeEditorInput(value);
      } else if (typeof value === 'string') {
        sanitized[key] = this.sanitizeInput(value);
      } else if (typeof value === 'object') {
        sanitized[key] = this.sanitizePayload(value);
      } else {
        sanitized[key] = value;
      }
    }

    return sanitized;
  }
}
